import { DateTime } from 'luxon';
import {
  ProviderPauseLength,
  ScheduledPause,
} from '../../types/api/location.types';
import {
  toPauseUntil,
  wallClockToZone,
  zone,
  zoneToWallClock,
} from '../Menu/MenuProviderSections/Items/itemPause.helpers';

/**
 * How long a provider pause lasts: a number of minutes, `null` for the rest of
 * today (midnight in the location's zone, not closing time), or `until` a time
 * picked on the location's clock, today or tomorrow.
 */
export type ProviderPauseChoice = number | null | 'until';

export const PROVIDER_PAUSE_OPTIONS: {
  name: string;
  value: ProviderPauseChoice;
}[] = [
  { name: 'Rest of Today', value: null },
  { name: '2 hours', value: 120 },
  { name: '1 hour', value: 60 },
  { name: '30 mins', value: 30 },
  { name: 'Custom Time', value: 'until' },
];

/*
 * Like the item pause, the picker hands back a wall clock: its fields are the
 * time chosen on the location's clock, its zone means nothing.
 */

/** Today 00:00 to tomorrow 23:59 on the location's clock: all a pause may span. */
export const pauseUntilBounds = (timeZone?: string) => {
  const today = DateTime.now().setZone(zone(timeZone)).startOf('day');
  return {
    minDate: zoneToWallClock(today),
    maxDate: zoneToWallClock(today.plus({ days: 1 }).endOf('day')),
  };
};

/** Same 400s the backend answers, caught before the request. */
export const pauseUntilError = (
  wallClock: Date | undefined,
  timeZone?: string
) => {
  if (!wallClock) return 'Please pick a time';
  const end = wallClockToZone(wallClock, timeZone);
  if (end <= DateTime.now()) return 'Please pick a time in the future';
  const lastDay = DateTime.now().setZone(zone(timeZone)).plus({ days: 1 });
  if (end > lastDay.endOf('day')) {
    return 'A pause can end tomorrow at the latest. Use a closing day for longer.';
  }
  return null;
};

/**
 * When a `Custom Time` pause starts: `now`, or `later` at a picked time. The
 * ready lengths (Rest of Today, 2 hours, ...) always start now.
 */
export type ProviderPauseStart = 'now' | 'later';

/** The backend starts anything sooner than a minute away right now. */
const SCHEDULE_MIN_LEAD = { minutes: 1 };

/** Same 400s the backend answers for `pauseFrom`, caught before the request. */
export const pauseFromError = (
  wallClock: Date | undefined,
  timeZone?: string
) => {
  if (!wallClock) return 'Please pick a start time';
  const start = wallClockToZone(wallClock, timeZone);
  if (start <= DateTime.now().plus(SCHEDULE_MIN_LEAD)) {
    return 'Please pick a start time in the future';
  }
  const lastDay = DateTime.now().setZone(zone(timeZone)).plus({ days: 1 });
  if (start > lastDay.endOf('day')) {
    return 'A pause can start tomorrow at the latest. Use a closing day for later.';
  }
  return null;
};

export const PAUSE_END_BEFORE_START_ERROR =
  'The pause has to end after it starts';

/** Both ends of a scheduled pause, and the end after the start. */
export const scheduledPauseError = (
  from: Date | undefined,
  until: Date | undefined,
  timeZone?: string
) => {
  const error =
    pauseFromError(from, timeZone) ?? pauseUntilError(until, timeZone);
  if (error || !from || !until) return error;
  return wallClockToZone(until, timeZone) <= wallClockToZone(from, timeZone)
    ? PAUSE_END_BEFORE_START_ERROR
    : null;
};

/** Where the end picker goes when a later start passes it: an hour after. */
export const defaultScheduledEnd = (from: Date) =>
  DateTime.fromJSDate(from).plus({ hours: 1 }).toJSDate();

export const providerPauseLength = (
  choice: ProviderPauseChoice,
  until: Date | undefined,
  from?: Date
): ProviderPauseLength =>
  choice === 'until' && until
    ? {
        pauseUntil: toPauseUntil(until),
        ...(from ? { pauseFrom: toPauseUntil(from) } : {}),
      }
    : { minutes: choice === 'until' ? null : choice };

/** "5:00 PM", "2:00 AM tomorrow", or the date past that, on the location's clock. */
export const pauseEndLabel = (end: DateTime, timeZone?: string) => {
  const there = end.setZone(zone(timeZone));
  const today = DateTime.now().setZone(zone(timeZone));
  const time = there.toFormat('h:mm a');
  if (there.hasSame(today, 'day')) return time;
  if (there.hasSame(today.plus({ days: 1 }), 'day')) return `${time} tomorrow`;
  return there.toFormat('EEE, MMM d, h:mm a');
};

/** The picked time as the confirm button repeats it. */
export const pickedPauseEndLabel = (wallClock: Date, timeZone?: string) =>
  pauseEndLabel(wallClockToZone(wallClock, timeZone), timeZone);

/**
 * `pausedUntil` while it still holds. The platforms end a pause on their own
 * and nothing tells us, so a past one reads as not paused anymore.
 */
export const activePauseEnd = (pausedUntil?: string | null) => {
  if (!pausedUntil) return null;
  const end = DateTime.fromISO(pausedUntil);
  return end.isValid && end > DateTime.now() ? end : null;
};

/** "Paused until 5:00 PM", or null when the row is not paused from our side. */
export const pausedUntilLabel = (
  pausedUntil: string | null | undefined,
  timeZone?: string
) => {
  const end = activePauseEnd(pausedUntil);
  return end ? `Paused until ${pauseEndLabel(end, timeZone)}` : null;
};

/** "Today", "Tomorrow" or the date, for the day a pause starts. */
const dayLabel = (moment: DateTime, timeZone?: string) => {
  const today = DateTime.now().setZone(zone(timeZone));
  if (moment.hasSame(today, 'day')) return 'Today';
  if (moment.hasSame(today.plus({ days: 1 }), 'day')) return 'Tomorrow';
  return moment.toFormat('EEE, MMM d');
};

/**
 * "Today, 2:00 PM - 3:00 PM" on the location's clock. An end past midnight
 * reads "10:00 PM - 2:00 AM", the end being after the start says the rest.
 */
export const pauseRangeLabel = (
  start: DateTime,
  end: DateTime,
  timeZone?: string
) => {
  const from = start.setZone(zone(timeZone));
  const to = end.setZone(zone(timeZone));
  return `${dayLabel(from, timeZone)}, ${from.toFormat(
    'h:mm a'
  )} - ${to.toFormat('h:mm a')}`;
};

/** The picked start and end, as the confirm button and the toast repeat them. */
export const pickedPauseRangeLabel = (
  from: Date,
  until: Date,
  timeZone?: string
) =>
  pauseRangeLabel(
    wallClockToZone(from, timeZone),
    wallClockToZone(until, timeZone),
    timeZone
  );

export const scheduledPauseLabel = (pause: ScheduledPause, timeZone?: string) =>
  pauseRangeLabel(
    DateTime.fromISO(pause.startAt, { setZone: true }),
    DateTime.fromISO(pause.endAt, { setZone: true }),
    timeZone
  );

/** "2:00 PM", "2:00 PM tomorrow": when a scheduled pause starts. */
export const scheduledPauseStartLabel = (
  pause: ScheduledPause,
  timeZone?: string
) => pauseEndLabel(DateTime.fromISO(pause.startAt), timeZone);

/**
 * "2:00 - 3:00 PM", "11:00 AM - 1:00 PM", with "tomorrow" in front when it
 * starts then: the confirm button's short form.
 */
export const pickedPauseTimesLabel = (
  from: Date,
  until: Date,
  timeZone?: string
) => {
  const start = wallClockToZone(from, timeZone);
  const end = wallClockToZone(until, timeZone);
  const sameHalf =
    start.hasSame(end, 'day') && start.toFormat('a') === end.toFormat('a');
  const times = `${start.toFormat(
    sameHalf ? 'h:mm' : 'h:mm a'
  )} - ${end.toFormat('h:mm a')}`;
  const day = dayLabel(start, timeZone);
  return day === 'Today' ? times : `${day.toLowerCase()} ${times}`;
};
